import React, { useLayoutEffect, lazy, Suspense } from "react";
import { ChakraProvider, Box, Badge, Flex, Tooltip, IconButton, Center } from "@chakra-ui/react";
import theme from "./theme";
import { ChatIcon, CloseIcon } from "@chakra-ui/icons";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { FaClipboardList } from "react-icons/fa";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./queryClient";

// Structural — always loaded
import Sidebar from "./components/Sidebar";
import ProtectedRoute from "./components/ProtectedRoute";
import PermissionRoute from "./components/PermissionRoute";
import RootRoute from "./components/RootRoute";
import SidebarROOT from "./modules/ROOT/Sidebar";
import LegalAcceptanceGate from "./components/Legal/LegalAcceptanceGate";
// import ChatWidget from "./components/Chat/ChatWidget";
import ChatWidgetV2 from "./components/Chat/ChatWidgetV2";
import TarefaRecadoDrawer from "./components/Agenda/TarefaRecadoSidebar/TarefaRecadoDrawer";
import ConfiguracaoInicialWidget from "./modules/Home/RequisitosMinimos/components/ConfiguracaoInicialWidget";

// Context
import { ClinicProvider, useClinic } from "./components/Context/ClinicaContext";
import { UserProvider } from "./components/Context/UserContext";
import { TelemedicinaProvider } from "./context/TelemedicinaContext";
import { Spinner } from "./components";


// Lazy — public routes
const LoginScreen = lazy(() => import("./screens/Login/LoginScreen"));
const ErrorScreen = lazy(() => import("./screens/Error/ErrorScreen"));
const RedefinirSenhaScreen = lazy(() => import("./screens/Login/RedefinirSenhaScreen"));
const PrimeiroAcessoScreen = lazy(() => import("./screens/Login/PrimeiroAcessoScreen"));
const TermosDeServicoPage = lazy(() => import("./components/Legal/TermosDeServicoPage"));
const PoliticaPrivacidadePage = lazy(() => import("./components/Legal/PoliticaPrivacidadePage"));
const SatisfacaoScreen = lazy(() => import("./screens/Marketing/SatisfacaoScreen"));
const TelemedicinaEntrar = lazy(() => import("./components/Anamnese/Telemedicina/TelemedicinaEntrar"));
const TelemedicinaFloatingWindow = lazy(() => import("./components/Anamnese/Telemedicina/TelemedicinaFloatingWindow"));

// Lazy — ROOT
const RootHome = lazy(() => import("./screens/ROOT/home"));
const LicencaScreen = lazy(() => import("./screens/ROOT/LicencaScreen"));

// Lazy — Agenda
const Agenda = lazy(() => import("./components/Agenda/Agenda"));
const ListaEspera = lazy(() => import("./components/Agenda/FilaEspera/ListaEspera"));

// Lazy — Pacientes
const PatientsList = lazy(() => import("./components/Pacientes/PatientsList"));
const NewPatient = lazy(() => import("./components/Pacientes/CadastroPaciente/NewPatient"));
const DeletionHistory = lazy(() => import("./components/Pacientes/DeletionHistory"));

// Lazy — Configurações
const ConfiguracoesContasVinculadas = lazy(() => import("./components/Configuracoes/ConfiguracoesContasVinculadas"));
const ConfiguracoesWhatsAppAScreen = lazy(() => import("./screens/Configuracoes/ConfiguracoesWhatsApp"));
const AgendamentoOnline = lazy(() => import("./screens/Configuracoes/AgendamentoOnline"));
const AgendamentoOnlinePaciente = lazy(() => import("./screens/Configuracoes/AgendamentoOnlinePaciente"));
const Parametros = lazy(() => import("./screens/Configuracoes/Parametros"));

// Lazy — Prontuários / Anamnese
const AnamneseMain = lazy(() => import("./components/Anamnese/AnamneseMain"));
const ResumoAtendimento = lazy(() => import("./components/Anamnese/ResumoAtendimento"));
const AnamneseGrid = lazy(() => import("./components/Anamnese/AnamneseGrid"));
const ModeloAnamneseList = lazy(() => import("./components/Anamnese/ModeloAnamneseList"));
const ModeloAnamneseListPadrao = lazy(() => import("./components/Anamnese/ModeloAnamneseListPadrao"));
const ModeloAnamneseForm = lazy(() => import("./components/Anamnese/ModeloAnamneseForm"));

// Lazy — Documentos
const ModeloDocumento = lazy(() => import("./components/ProfissionalSaude/ModeloDocumento"));
const ModeloDocumentoForm = lazy(() => import("./components/ProfissionalSaude/ModeloDocumentoForm"));
const ConfiguracoesImpressaoDocumento = lazy(() => import("./components/ProfissionalSaude/ConfiguracoesImpressaoDocumento"));
const TiposDocumentoList = lazy(() => import("./components/ProfissionalSaude/TiposDocumentoList"));
const CadastroTipoDocumento = lazy(() => import("./components/ProfissionalSaude/CadastroTipoDocumento"));
const ListaDocumentosEmitidos = lazy(() => import("./components/ProfissionalSaude/ListaDocumentosEmitidos"));

// Lazy — Finanças
const FluxoCaixa = lazy(() => import("./components/Financas/FluxoCaixa/FluxoCaixa"));
const DreGerencialPage = lazy(() => import("./components/Financas/DreGerencial/DreGerencialPage"));
const ApuracaoFiscalEstoqueTela = lazy(() => import("./modules/Financas/ApuracaoFiscalEstoque/components/ApuracaoFiscalEstoqueTela"));
const GestaoPlanoScreen = lazy(() => import("./screens/Financas/GestaoPlanoScreen"));
const FornecedoresList = lazy(() => import("./components/Financas/Fornecedores/FornecedoresList"));
const GestaoProdutosScreen = lazy(() => import("./screens/Financas/GestaoProdutosScreen"));
const ProductList = lazy(() => import("./screens/Financas/ProdutoListScreen"));
const ProdutoFormScreen = lazy(() => import("./screens/Financas/ProdutoFormScreen"));
const CategoriasProdutoScreen = lazy(() => import("./screens/Financas/CategoriasProdutoScreen"));
const OpcoesProdutoScreen = lazy(() => import("./screens/Financas/OpcoesProdutoScreen"));
const FabricantesScreen = lazy(() => import("./screens/Financas/FabricantesScreen"));
const ProdutoEntradaFormScreen = lazy(() => import("./screens/Financas/ProdutoEntradaFormScreen"));
const ProdutoEntradaList = lazy(() => import("./screens/Financas/ProdutoEntradaListScreen"));
const ProdutoSaidaList = lazy(() => import("./screens/Financas/ProdutoSaidaListScreen"));
const ProdutoSaidaFormScreen = lazy(() => import("./screens/Financas/ProdutoSaidaFormScreen"));
const EstoqueList = lazy(() => import("./screens/Financas/EstoqueListScreen"));
const AlmoxarifadoLocaisScreen = lazy(() => import("./screens/Financas/AlmoxarifadoLocaisScreen"));
const RegrasFiscaisScreen = lazy(() => import("./screens/Financas/RegrasFiscaisScreen"));
const CatalogoFiscalScreen = lazy(() => import("./screens/Financas/CatalogoFiscalScreen"));
const DadosFiscaisUnidadeScreen = lazy(() => import("./screens/Financas/DadosFiscaisUnidadeScreen"));
const PendenciasFiscaisScreen = lazy(() => import("./screens/Financas/PendenciasFiscaisScreen"));
const PerfilFiscalProdutoScreen = lazy(() => import("./screens/Financas/PerfilFiscalProdutoScreen"));
const CentroCusto = lazy(() => import("./components/Financas/CentroCusto/CentroCusto"));
const ContasAPagarList = lazy(() => import("./components/Financas/Despesas/ContasPagarList"));
const ContasReceberList = lazy(() => import("./components/Financas/Receita/ContasReceberList"));
const ContaBancariaScreen = lazy(() => import("./screens/Financas/ContaBancariaScreen"));
const ConciliacaoBancaria = lazy(() => import("./components/Financas/ConciliacaoBancaria/ConciliacaoBancaria"));
const FormaPagamentoListScreen = lazy(() => import("./screens/Financas/FormaPagamentoListScreen"));
const DashboardFinanceiroScreen = lazy(() => import("./screens/Financas/DashboardFinanceiroScreen"));
const DescontosScreen = lazy(() => import("./screens/Financas/DescontosScreen"));

// Lazy — ADM
const ConvenioScreen = lazy(() => import("./screens/ADM/ConvenioScreen"));
const ColaboradorScreen = lazy(() => import("./screens/ADM/ColaboradorScreen"));
const NovoTurnoTrabalho = lazy(() => import("./components/ADM/Cadastros/CadastroTurnoTrabalho"));
const TurnoTrabalho = lazy(() => import("./components/ADM/Listas/TurnoTrabalhoList"));
const ProcedimentoListScreen = lazy(() => import("./screens/ADM/ProcedimentoListScreen"));
const PacotesPlanosScreen = lazy(() => import("./screens/ADM/PacotesPlanosScreen"));
const EspecialidadeListScreen = lazy(() => import("./screens/ADM/EspecialidadeListScreen"));
const TabelaProcedimentoListScreen = lazy(() => import("./screens/ADM/TabelaProcedimentoListScreen"));
const TabelaProcedimentoItensScreen = lazy(() => import("./screens/ADM/TabelaProcedimentoItensScreen"));
const RelatorioProfissionalSaudeScreen = lazy(() => import("./screens/ADM/RelatorioProfisionalSaudeScreen"));
const RelatorioIsencoesScreen = lazy(() => import("./screens/ADM/RelatorioIsencoesScreen"));
const ProfissionalSaudeScreen = lazy(() => import("./screens/ADM/ProfissionalSaudeScreen"));
const ComissaoScreen = lazy(() => import("./screens/ADM/ComissaoScreen"));
const ComissaoFormScreen = lazy(() => import("./screens/ADM/ComissaoFormScreen"));
const ClinicaListScreen = lazy(() => import("./screens/ADM/ClinicaScreen"));
const SalasScreen = lazy(() => import("./screens/ADM/SalasScreen"));
const DashboardAdministrativoScreen = lazy(() => import("./screens/ADM/DashboardAdministrativoScreen"));
const UserScreen = lazy(() => import("./screens/User/UserScreen"));
const PerfilScreen = lazy(() => import("./screens/User/PerfilScreen"));

const Home = lazy(() => import("./screens/Home"));

// Lazy — Marketing / CRM
const Lead = lazy(() => import("./screens/Marketing/Lead"));
const FunilDeVendas = lazy(() => import("./screens/Marketing/FunilDeVendas"));
const MarketingDashboard = lazy(() => import("./screens/Marketing/MarketingDashboard"));
const NPSScreen = lazy(() => import("./screens/Marketing/NPS"));

// Lazy — Convênio
const TabelaProcedimentosConveninioScreen = lazy(() => import("./screens/Convenio/TabelaProcedimentosConvenio"));

// Lazy — Notificações
const NotificacoesPage = lazy(() => import("./components/Notificacoes/NotificacoesPage"));

const PageSpinner = () => <Spinner />;

function ScrollToTop() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    const el = document.getElementById("main-content");
    if (el) el.scrollTop = 0;
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function GlobalFloatingWidgets() {
  const { selectedClinic } = useClinic();
  const clinicaId = selectedClinic?._id || "";

  return (
    <>
      <ConfiguracaoInicialWidget />
      <ChatWidgetV2 clinicaId={clinicaId}>
        {({ onOpen, isOpen, onlineCount }) => (
          <Box position="fixed" right="16px" bottom="16px" zIndex={1100}>
            <Tooltip label="Chat interno" placement="left">
              <IconButton
                aria-label="Abrir chat interno"
                colorScheme="teal"
                size="lg"
                icon={isOpen ? <CloseIcon /> : <ChatIcon />}
                onClick={onOpen}
              />
            </Tooltip>
            {onlineCount > 0 && (
              <Badge
                position="absolute"
                top="-6px"
                right="-6px"
                colorScheme="red"
                borderRadius="full"
                px="6px"
                py="2px"
                fontSize="xs"
              >
                {onlineCount}
              </Badge>
            )}
          </Box>
        )}
      </ChatWidgetV2>
      <TarefaRecadoDrawer clinicaId={clinicaId}>
        {({ onOpen, totalBadge }) => (
          <Box position="fixed" right="16px" bottom="80px" zIndex={1100}>
            <Tooltip label="Tarefas/Recados" placement="left">
              <Box position="relative" display="inline-block">
                <IconButton
                  aria-label="Abrir tarefas/recados"
                  colorScheme="teal"
                  size="lg"
                  icon={<FaClipboardList />}
                  onClick={onOpen}
                />
                {totalBadge > 0 ? (
                  <Badge
                    position="absolute"
                    top="-6px"
                    right="-6px"
                    colorScheme="red"
                    borderRadius="full"
                    fontSize="0.65rem"
                    minW="1.25rem"
                    textAlign="center"
                  >
                    {totalBadge > 99 ? "99+" : totalBadge}
                  </Badge>
                ) : null}
              </Box>
            </Tooltip>
          </Box>
        )}
      </TarefaRecadoDrawer>
      <Suspense fallback={null}>
        <TelemedicinaFloatingWindow />
      </Suspense>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ChakraProvider theme={theme}>
        <Router>
          <UserProvider>
            <ClinicProvider>
              <Suspense fallback={<PageSpinner />}>
                <Routes>
                  <Route path="/login" element={<LoginScreen />} />
                  <Route path="/erro/:code" element={<ErrorScreen />} />
                  <Route path="/redefinir-senha" element={<RedefinirSenhaScreen />} />
                  <Route path="/primeiro-acesso" element={<PrimeiroAcessoScreen />} />
                  <Route path="/agendamento-online/:token" element={<AgendamentoOnlinePaciente />} />
                  <Route
                    path="/telemedicina-entrar/:salaId"
                    element={<TelemedicinaEntrar />}
                  />
                  <Route path="/satisfacao" element={<SatisfacaoScreen />} />
                  <Route path="/termos-de-servico" element={<TermosDeServicoPage />} />
                  <Route path="/politica-de-privacidade" element={<PoliticaPrivacidadePage />} />
                  <Route element={<RootRoute />}>
                    <Route
                      path="/manager"
                      element={
                        <Flex minH="100vh" align="stretch">
                          <SidebarROOT />
                          <Box flex="1" minW="0" p={5} overflowX="hidden" overflowY="auto">
                            <Outlet />
                          </Box>
                        </Flex>
                      }
                    >
                      <Route index element={<RootHome />} />
                      <Route path="licenca" element={<LicencaScreen />} />
                    </Route>
                  </Route>
                  <Route element={<ProtectedRoute />}>
                    <Route
                      path="*"
                      element={
                        <TelemedicinaProvider>
                          <LegalAcceptanceGate>
                            <Flex
                              w="100%"
                              maxW="100dvw"
                              minW={0}
                              minH="100dvh"
                              boxSizing="border-box"
                              direction={{ base: "column", md: "row" }}
                              align="stretch"
                              bg="white"
                            >
                              <Sidebar />
                              <ScrollToTop />
                              <Box
                                id="main-content"
                                flex="1"
                                w={{ base: "100%", md: "auto" }}
                                maxW="100%"
                                boxSizing="border-box"
                                sx={{ contain: "inline-size" }}
                                minW="0"
                                minH="0"
                                p={3}
                                overflowX="hidden"
                                overflowY="auto"
                                display="flex"
                                flexDirection="column"
                                bg="white"
                              >
                                <Routes>
                                  <Route path="/" element={<Navigate to="/agenda" replace />} />
                                  <Route path="/home" element={<Home />} />
                                  <Route path="/agenda" element={<PermissionRoute perm={['agenda', 'agenda']}><Agenda /></PermissionRoute>} />

                                  {/* Pacientes */}
                                  <Route path="/pacientes" element={<PermissionRoute perm={['pacientes', 'listaPacientes']}><PatientsList /></PermissionRoute>} />
                                  <Route path="/historico-exclusoes" element={<PermissionRoute perm={['pacientes', 'listaPacientes']}><DeletionHistory /></PermissionRoute>} />
                                  <Route path="/novo-paciente" element={<PermissionRoute perm={['pacientes', 'listaPacientes']}><NewPatient /></PermissionRoute>} />
                                  <Route path="/editpatient/:id" element={<PermissionRoute perm={['pacientes', 'listaPacientes']}><NewPatient /></PermissionRoute>} />
                                  <Route path="/lista-espera" element={<PermissionRoute perm={['pacientes', 'listaEspera']}><ListaEspera /></PermissionRoute>} />

                                  {/* Configurações */}
                                  <Route path="/configuracoes/google-calendar" element={<PermissionRoute perm={['configuracoes', 'vinculadas']}><ConfiguracoesContasVinculadas /></PermissionRoute>} />
                                  <Route path="/configuracoes/whatsapp" element={<PermissionRoute perm={['configuracoes', 'vinculadas']}><ConfiguracoesWhatsAppAScreen /></PermissionRoute>} />
                                  <Route path="/configuracoes/agendamentoOnline" element={<PermissionRoute perm={['configuracoes', 'agendamentoOnline']}><AgendamentoOnline /></PermissionRoute>} />
                                  <Route path="/configuracoes/Parametros" element={<PermissionRoute perm={['configuracoes', 'parametro']}><Parametros /></PermissionRoute>} />

                                  {/* Prontuários */}
                                  <Route path="/anamnese-main" element={<PermissionRoute perm={['prontuarios', 'anamnese']} allowedRoles={['ProfissionalSaude']}><AnamneseMain /></PermissionRoute>} />
                                  <Route path="/resumo-atendimento" element={<PermissionRoute perm={['prontuarios', 'anamnese']} allowedRoles={['ProfissionalSaude']}><ResumoAtendimento /></PermissionRoute>} />

                                  {/* Finanças */}
                                  <Route path="/financas/fluxocaixa" element={<PermissionRoute perm={['financas', 'fluxoCaixa']}><FluxoCaixa /></PermissionRoute>} />
                                  <Route path="/financas/dre" element={<PermissionRoute perm={['financas', 'dre']}><DreGerencialPage /></PermissionRoute>} />
                                  <Route path="/financas/apuracao-fiscal-estoque" element={<PermissionRoute perm={['financas', 'fiscal']}><ApuracaoFiscalEstoqueTela /></PermissionRoute>} />
                                  <Route path="/financas/gestao-planos" element={<PermissionRoute perm={['administrativo', 'gerenciamentoPlanos']}><GestaoPlanoScreen /></PermissionRoute>} />
                                  <Route path="/financas/fornecedores" element={<PermissionRoute perm={['financas', 'fornecedores']}><FornecedoresList /></PermissionRoute>} />
                                  <Route path="/financas/gestao-produtos" element={<PermissionRoute perm={['almoxarifado', 'cadastroProduto']}><GestaoProdutosScreen /></PermissionRoute>} />
                                  <Route path="/financas/produtoslistas" element={<PermissionRoute perm={['almoxarifado', 'cadastroProduto']}><ProductList /></PermissionRoute>} />
                                  <Route path="/financas/produtos/novo" element={<PermissionRoute perm={['almoxarifado', 'cadastroProduto']}><ProdutoFormScreen /></PermissionRoute>} />
                                  <Route path="/financas/produtos/:id/editar" element={<PermissionRoute perm={['almoxarifado', 'cadastroProduto']}><ProdutoFormScreen /></PermissionRoute>} />
                                  <Route path="/financas/categorias" element={<PermissionRoute perm={['almoxarifado', 'cadastroProduto']}><CategoriasProdutoScreen /></PermissionRoute>} />
                                  <Route path="/financas/opcoes-produto" element={<PermissionRoute perm={['almoxarifado', 'cadastroProduto']}><OpcoesProdutoScreen /></PermissionRoute>} />
                                  <Route path="/financas/tamanhos" element={<Navigate to="/financas/opcoes-produto" replace />} />
                                  <Route path="/financas/fabricantes" element={<PermissionRoute perm={['almoxarifado', 'cadastroProduto']}><FabricantesScreen /></PermissionRoute>} />
                                  <Route path="/financas/entradaproduto" element={<PermissionRoute perm={['almoxarifado', 'entradaProduto']}><ProdutoEntradaList /></PermissionRoute>} />
                                  <Route path="/financas/entradaproduto/novo" element={<PermissionRoute perm={['almoxarifado', 'entradaProduto']}><ProdutoEntradaFormScreen /></PermissionRoute>} />
                                  <Route path="/financas/entradaproduto/:id/editar" element={<PermissionRoute perm={['almoxarifado', 'entradaProduto']}><ProdutoEntradaFormScreen /></PermissionRoute>} />
                                  <Route path="/financas/saidaproduto" element={<PermissionRoute perm={['almoxarifado', 'saidaProduto']}><ProdutoSaidaList /></PermissionRoute>} />
                                  <Route path="/financas/saidaproduto/novo" element={<PermissionRoute perm={['almoxarifado', 'saidaProduto']}><ProdutoSaidaFormScreen /></PermissionRoute>} />
                                  <Route path="/financas/estoque" element={<PermissionRoute perm={['almoxarifado', 'estoque']}><EstoqueList /></PermissionRoute>} />
                                  <Route path="/financas/almoxarifados" element={<PermissionRoute perm={['almoxarifado', 'almoxarifados']}><AlmoxarifadoLocaisScreen /></PermissionRoute>} />
                                  <Route path="/financas/regras-fiscais" element={<PermissionRoute perm={['financas', 'fiscal']}><RegrasFiscaisScreen /></PermissionRoute>} />
                                  <Route path="/financas/catalogo-fiscal" element={<PermissionRoute perm={['financas', 'fiscal']}><CatalogoFiscalScreen /></PermissionRoute>} />
                                  <Route path="/financas/dados-fiscais" element={<PermissionRoute perm={['financas', 'fiscal']}><DadosFiscaisUnidadeScreen /></PermissionRoute>} />
                                  <Route path="/financas/pendencias-fiscais" element={<PermissionRoute perm={['financas', 'fiscal']}><PendenciasFiscaisScreen /></PermissionRoute>} />
                                  <Route path="/financas/perfil-fiscal-produto" element={<PermissionRoute perm={['financas', 'fiscal']}><PerfilFiscalProdutoScreen /></PermissionRoute>} />
                                  <Route path="/financas/centrocusto" element={<PermissionRoute perm={['financas', 'centroCusto']}><CentroCusto /></PermissionRoute>} />
                                  <Route path="/financas/contaspagar" element={<PermissionRoute perm={['financas', 'contasPagar']}><ContasAPagarList /></PermissionRoute>} />
                                  <Route path="/financas/contasreceber" element={<PermissionRoute perm={['financas', 'contasReceber']}><ContasReceberList /></PermissionRoute>} />
                                  <Route path="/financas/contasbancarias" element={<PermissionRoute perm={['financas', 'contasBancarias']}><ContaBancariaScreen /></PermissionRoute>} />
                                  <Route path="/financas/conciliacao" element={<PermissionRoute perm={['financas', 'conciliacao']}><ConciliacaoBancaria /></PermissionRoute>} />
                                  <Route path="/financas/formapagamento" element={<PermissionRoute perm={['financas', 'formaPagamento']}><FormaPagamentoListScreen /></PermissionRoute>} />
                                  <Route path="/financas/dashboard" element={<PermissionRoute perm={['dashboard', 'financeiro']}><DashboardFinanceiroScreen /></PermissionRoute>} />
                                  <Route path="/financas/descontos" element={<PermissionRoute perm={['financas', 'descontos']}><DescontosScreen /></PermissionRoute>} />

                                  {/* ADM */}
                                  <Route path="/adm/clinica" element={<PermissionRoute perm={['administrativo', 'clinicas']}><ClinicaListScreen /></PermissionRoute>} />
                                  <Route path="/adm/salas" element={<PermissionRoute perm={['agenda', 'mapaDeSala']}><SalasScreen /></PermissionRoute>} />
                                  <Route path="/adm/dashboard" element={<PermissionRoute perm={['dashboard', 'administrativo']}><DashboardAdministrativoScreen /></PermissionRoute>} />
                                  <Route path="/adm/colaboradores" element={<PermissionRoute perm={['administrativo', 'colaboradores']}><ColaboradorScreen /></PermissionRoute>} />
                                  <Route path="/adm/turnotrabalho" element={<PermissionRoute perm={['administrativo', 'turnoTrabalho']}><TurnoTrabalho /></PermissionRoute>} />
                                  <Route path="/adm/novoturnotrabalho" element={<PermissionRoute perm={['administrativo', 'turnoTrabalho']}><NovoTurnoTrabalho /></PermissionRoute>} />
                                  <Route path="/adm/edittornotrabalho/:id" element={<PermissionRoute perm={['administrativo', 'turnoTrabalho']}><NovoTurnoTrabalho /></PermissionRoute>} />
                                  <Route path="/adm/convenios" element={<PermissionRoute perm={['convenios', 'cadastro']}><ConvenioScreen /></PermissionRoute>} />
                                  <Route path="/adm/procedimentos" element={<PermissionRoute perm={['administrativo', 'procedimentos']}><ProcedimentoListScreen /></PermissionRoute>} />
                                  <Route path="/adm/especialidades" element={<PermissionRoute perm={['administrativo', 'especialidades']}><EspecialidadeListScreen /></PermissionRoute>} />
                                  <Route path="/adm/tabelaprocedimentos" element={<PermissionRoute perm={['administrativo', 'tabelaProcedimentos']}><TabelaProcedimentoListScreen /></PermissionRoute>} />
                                  <Route path="/adm/tabelaprocedimentos/:id" element={<PermissionRoute perm={['administrativo', 'tabelaProcedimentos']}><TabelaProcedimentoItensScreen /></PermissionRoute>} />
                                  <Route path="/adm/pacotesplanos" element={<PermissionRoute perm={['administrativo', 'pacotes']}><PacotesPlanosScreen /></PermissionRoute>} />
                                  <Route path="/adm/prossaudes" element={<PermissionRoute perm={['administrativo', 'profissionaisSaude']}><ProfissionalSaudeScreen /></PermissionRoute>} />
                                  <Route path="/adm/repassescomissoes" element={<PermissionRoute perm={['administrativo', 'repasses']}><ComissaoScreen /></PermissionRoute>} />
                                  <Route path="/adm/repassescomissoes/novo" element={<PermissionRoute perm={['administrativo', 'repasses']}><ComissaoFormScreen /></PermissionRoute>} />
                                  <Route path="/adm/repassescomissoes/:id/editar" element={<PermissionRoute perm={['administrativo', 'repasses']}><ComissaoFormScreen /></PermissionRoute>} />
                                  <Route path="/adm/controleusuarios" element={<PermissionRoute perm={['configuracoes', 'controleUsuario']}><UserScreen /></PermissionRoute>} />
                                  <Route path="/perfil" element={<PerfilScreen />} />
                                  <Route path="/adm/relatorios-profissional" element={<PermissionRoute perm={['administrativo', 'relatorios']}><RelatorioProfissionalSaudeScreen /></PermissionRoute>} />
                                  <Route path="/adm/relatorio-isencoes" element={<PermissionRoute perm={['administrativo', 'relatorios']}><RelatorioIsencoesScreen /></PermissionRoute>} />

                                  {/* Marketing */}
                                  <Route path="/marketing/funil-vendas" element={<PermissionRoute perm={['crm', 'funilVendas']}><FunilDeVendas /></PermissionRoute>} />
                                  <Route path="/marketing/leads" element={<PermissionRoute perm={['crm', 'leads']}><Lead /></PermissionRoute>} />
                                  <Route path="/marketing/dashboard" element={<PermissionRoute perm={['dashboard', 'marketing']}><MarketingDashboard /></PermissionRoute>} />
                                  <Route path="/marketing/nps" element={<PermissionRoute perm={['crm', 'nps']}><NPSScreen /></PermissionRoute>} />

                                  {/* Prontuários / Anamnese */}
                                  <Route path="/prosaude/anamnese" element={<PermissionRoute perm={['prontuarios', 'anamnese']}><AnamneseGrid /></PermissionRoute>} />
                                  <Route path="/prosaude/anamnese-padrao" element={<PermissionRoute perm={['prontuarios', 'anamnese']}><ModeloAnamneseListPadrao /></PermissionRoute>} />
                                  <Route path="/prosaude/anamnese-padrao/editar/:id" element={<PermissionRoute perm={['prontuarios', 'anamnese']}><ModeloAnamneseForm /></PermissionRoute>} />
                                  <Route path="/prosaude/anamnese-pediatrica" element={<Navigate to="/prosaude/anamnese-padrao" replace />} />
                                  <Route path="/prosaude/anamnese-pediatrica/editar/:id" element={<PermissionRoute perm={['prontuarios', 'anamnese']}><ModeloAnamneseForm /></PermissionRoute>} />
                                  <Route path="/prosaude/anamnese-osteopatia-adulta" element={<Navigate to="/prosaude/anamnese-padrao" replace />} />
                                  <Route path="/prosaude/anamnese-osteopatia-adulta/editar/:id" element={<PermissionRoute perm={['prontuarios', 'anamnese']}><ModeloAnamneseForm /></PermissionRoute>} />
                                  <Route path="/prosaude/anamnese-posturologia" element={<Navigate to="/prosaude/anamnese-padrao" replace />} />
                                  <Route path="/prosaude/anamnese-posturologia/editar/:id" element={<PermissionRoute perm={['prontuarios', 'anamnese']}><ModeloAnamneseForm /></PermissionRoute>} />
                                  <Route path="/prosaude/anamnese-oftalmologia/editar/:id" element={<PermissionRoute perm={['prontuarios', 'anamnese']}><ModeloAnamneseForm /></PermissionRoute>} />
                                  <Route path="/prosaude/anamnese-personalizada" element={<PermissionRoute perm={['prontuarios', 'anamnese']}><ModeloAnamneseList /></PermissionRoute>} />
                                  <Route path="/prosaude/anamnese-personalizada/novo" element={<PermissionRoute perm={['prontuarios', 'anamnese']}><ModeloAnamneseForm /></PermissionRoute>} />
                                  <Route path="/prosaude/anamnese-personalizada/editar/:id" element={<PermissionRoute perm={['prontuarios', 'anamnese']}><ModeloAnamneseForm /></PermissionRoute>} />

                                  {/* Documentos */}
                                  <Route path="/prosaude/modelodocumento" element={<PermissionRoute perm={['prontuarios', 'documentoPadrao']}><ModeloDocumento /></PermissionRoute>} />
                                  <Route path="/prosaude/modelodocumento/novo" element={<PermissionRoute perm={['prontuarios', 'documentoPadrao']}><ModeloDocumentoForm /></PermissionRoute>} />
                                  <Route path="/prosaude/modelodocumento/editar/:id" element={<PermissionRoute perm={['prontuarios', 'documentoPadrao']}><ModeloDocumentoForm /></PermissionRoute>} />
                                  <Route path="/prosaude/modelodocumento/configuracoes-impressao" element={<PermissionRoute perm={['prontuarios', 'documentoPadrao']}><ConfiguracoesImpressaoDocumento /></PermissionRoute>} />
                                  <Route path="/prosaude/documentos/:tipoSlug" element={<PermissionRoute perm={['prontuarios', 'documentoPadrao']}><ListaDocumentosEmitidos /></PermissionRoute>} />
                                  <Route path="/prosaude/tiposdocumento" element={<PermissionRoute perm={['prontuarios', 'documentoPadrao']}><TiposDocumentoList /></PermissionRoute>} />
                                  <Route path="/prosaude/tiposdocumento/novo" element={<PermissionRoute perm={['prontuarios', 'documentoPadrao']}><CadastroTipoDocumento /></PermissionRoute>} />
                                  <Route path="/prosaude/tiposdocumento/editar/:id" element={<PermissionRoute perm={['prontuarios', 'documentoPadrao']}><CadastroTipoDocumento /></PermissionRoute>} />

                                  {/* Convênio */}
                                  <Route path="/convenio/tabelaprocedimentos" element={<PermissionRoute perm={['convenios', 'tabelaProcedimentos']}><TabelaProcedimentosConveninioScreen /></PermissionRoute>} />

                                  {/* Notificações */}
                                  <Route path="/notificacoes" element={<PermissionRoute perm={['agenda', 'notificacoes']}><NotificacoesPage /></PermissionRoute>} />

                                  <Route path="*" element={<Navigate to="/erro/404" replace />} />
                                </Routes>
                              </Box>
                              <GlobalFloatingWidgets />
                            </Flex>
                          </LegalAcceptanceGate>
                        </TelemedicinaProvider>
                      }
                    />
                  </Route>
                </Routes>
              </Suspense>
            </ClinicProvider>
          </UserProvider>
        </Router>
      </ChakraProvider>
    </QueryClientProvider>
  );
}

export default App;
